import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import * as api from './profileAgentsAPI.js';

// Async thunks

export const fetchAgentTypes = createAsyncThunk('profileAgents/fetchTypes', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getAgentTypes();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgents = createAsyncThunk('profileAgents/fetchAgents', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getAgents(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgentsWithStatus = createAsyncThunk('profileAgents/fetchAgentsWithStatus', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getAgentsWithStatus(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createAgent = createAsyncThunk('profileAgents/create', async ({ profileId, data }, { rejectWithValue }) => {
  try {
    const res = await api.createAgent(profileId, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const updateAgent = createAsyncThunk('profileAgents/update', async ({ profileId, agentId, data }, { rejectWithValue }) => {
  try {
    const res = await api.updateAgent(profileId, agentId, data);
    return { agentId, data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteAgent = createAsyncThunk('profileAgents/delete', async ({ profileId, agentId }, { rejectWithValue }) => {
  try {
    await api.deleteAgent(profileId, agentId);
    return agentId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchChats = createAsyncThunk('profileAgents/fetchChats', async ({ profileId, agentId }, { rejectWithValue }) => {
  try {
    const res = await api.getChats(profileId, agentId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const sendMessage = createAsyncThunk('profileAgents/sendMessage', async ({ profileId, agentId, message }, { rejectWithValue }) => {
  try {
    const res = await api.sendMessage(profileId, agentId, message);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Slice

const profileAgentsSlice = createSlice({
  name: 'profileAgents',
  initialState: {
    agentTypes: [],
    agents: [],
    agentCount: 0,
    agentLimit: 0,
    chats: [],
    selectedAgentId: null,
    loading: false,
    chatsLoading: false,
    error: null,
  },
  reducers: {
    setSelectedAgent: (state, { payload }) => {
      state.selectedAgentId = payload;
    },
    clearChats: (state) => {
      state.chats = [];
    },
    addOptimisticMessage: (state, { payload }) => {
      state.chats.push(payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Agent Types
      .addCase(fetchAgentTypes.fulfilled, (state, { payload }) => {
        state.agentTypes = payload;
      })

      // Agents list
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, { payload }) => {
        state.agents = payload.Agents || payload.agents || [];
        state.agentCount = payload.Count ?? payload.count ?? 0;
        state.agentLimit = payload.Limit ?? payload.limit ?? 0;
        state.loading = false;
      })
      .addCase(fetchAgents.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      // Agents list with VM status
      .addCase(fetchAgentsWithStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentsWithStatus.fulfilled, (state, { payload }) => {
        state.agents = payload.Agents || payload.agents || [];
        state.agentCount = payload.Count ?? payload.count ?? 0;
        state.agentLimit = payload.Limit ?? payload.limit ?? 0;
        state.loading = false;
      })
      .addCase(fetchAgentsWithStatus.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      // Create
      .addCase(createAgent.fulfilled, (state, { payload }) => {
        state.agents.push(payload);
        state.agentCount += 1;
      })

      // Delete
      .addCase(deleteAgent.fulfilled, (state, { payload }) => {
        state.agents = state.agents.filter((a) => a.Id !== payload && a.id !== payload);
        state.agentCount -= 1;
      })

      // Chats
      .addCase(fetchChats.pending, (state) => {
        state.chatsLoading = true;
      })
      .addCase(fetchChats.fulfilled, (state, { payload }) => {
        state.chats = payload;
        state.chatsLoading = false;
      })
      .addCase(fetchChats.rejected, (state) => {
        state.chatsLoading = false;
      })

      // Send message
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        // Message already added optimistically
      });
  },
});

export const { setSelectedAgent, clearChats: clearChatsAction, addOptimisticMessage } = profileAgentsSlice.actions;

// Selectors
export const selectAgentTypes = (state) => state.profileAgents.agentTypes;
export const selectAgents = (state) => state.profileAgents.agents;
export const selectAgentCount = (state) => state.profileAgents.agentCount;
export const selectAgentLimit = (state) => state.profileAgents.agentLimit;
export const selectChats = (state) => state.profileAgents.chats;
export const selectSelectedAgentId = (state) => state.profileAgents.selectedAgentId;
export const selectLoading = (state) => state.profileAgents.loading;
export const selectChatsLoading = (state) => state.profileAgents.chatsLoading;
export const selectError = (state) => state.profileAgents.error;

export default profileAgentsSlice.reducer;
